/**
 * VRMViewer — Three.js scene that loads and drives a VRM avatar.
 *
 * Tracking data arrives from the bridge in Unity's left-handed coordinate
 * system.  Before applying to Three.js bones we convert:
 *   position  → negate X  (-x, y, z)
 *   quaternion → negate X only  (-qx, qy, qz, qw)
 * Negating only X mirrors across the YZ plane (left-hand → right-hand).
 * Do NOT negate W — that would reverse the rotation direction and cause
 * bones like the arms to rotate the wrong way (e.g. lift instead of lower).
 *
 * Blend-shape / expression names are normalised to VRM 1.0 expression names
 * used by @pixiv/three-vrm v2 via VMC_TO_EXPR.  Three sources are supported:
 *
 *   VSeeFace       — sends VRM 0.x PascalCase preset names (A, Blink_L, Joy…)
 *   Warudo VRM 0.x — same PascalCase preset names as VSeeFace
 *   Warudo VRM 1.0 — sends VRM 1.0 camelCase names (aa, blinkLeft, happy…)
 *                    which pass through the fallback unchanged
 *   Warudo ARKit   — sends Apple ARKit blendshape names (eyeBlinkLeft,
 *                    jawOpen…) which are mapped to the nearest VRM 1.0 name
 */

import * as THREE from 'three'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { VRMLoaderPlugin, VRMUtils } from '@pixiv/three-vrm'

// ── Name mappings ─────────────────────────────────────────────────────────────

/**
 * Blend-shape / expression name → @pixiv/three-vrm v2 VRM 1.0 expression name.
 *
 * Covers three input vocabularies:
 *  1. VRM 0.x PascalCase preset names  (VSeeFace, Warudo with VRM 0.x model)
 *  2. ARKit blendshape names            (Warudo with ARKit face-tracker)
 *
 * VRM 1.0 camelCase names (Warudo with VRM 1.0 model) need no mapping —
 * they pass through the `VMC_TO_EXPR[vmcName] ?? vmcName` fallback unchanged.
 *
 * For paired ARKit shapes that share a VRM target (e.g. eyeLookUpLeft +
 * eyeLookUpRight → lookUp) the EMA smoother runs per-name so both values
 * converge; the last one written in the loop is the final setValue call —
 * both eyes should be tracking the same direction so the values are equal.
 */
const VMC_TO_EXPR = {
  // ── VRM 0.x preset names (VSeeFace / Warudo VRM 0.x) ─────────────────────
  A: 'aa', I: 'ih', U: 'ou', E: 'ee', O: 'oh',
  Neutral:  'neutral',
  Blink:    'blink',
  Blink_L:  'blinkLeft',
  Blink_R:  'blinkRight',
  Joy:      'happy',
  Angry:    'angry',
  Sorrow:   'sad',
  Fun:      'relaxed',
  LookUp:   'lookUp',
  LookDown: 'lookDown',
  LookLeft: 'lookLeft',
  LookRight:'lookRight',

  // ── ARKit blendshape names (Warudo ARKit face tracker) ────────────────────
  // Eyes — blink
  eyeBlinkLeft:     'blinkLeft',
  eyeBlinkRight:    'blinkRight',
  eyeWideLeft:      'surprised',
  eyeWideRight:     'surprised',
  // Eyes — gaze (per-eye → single VRM look direction)
  eyeLookUpLeft:    'lookUp',
  eyeLookUpRight:   'lookUp',
  eyeLookDownLeft:  'lookDown',
  eyeLookDownRight: 'lookDown',
  eyeLookInLeft:    'lookRight',  // left eye rotated inward  → rightward gaze
  eyeLookInRight:   'lookLeft',   // right eye rotated inward → leftward gaze
  eyeLookOutLeft:   'lookLeft',   // left eye rotated outward → leftward gaze
  eyeLookOutRight:  'lookRight',  // right eye rotated outward → rightward gaze
  // Jaw / mouth
  jawOpen:          'aa',
  mouthFunnel:      'ou',
  mouthPucker:      'ou',
  // Emotions
  mouthSmileLeft:   'happy',
  mouthSmileRight:  'happy',
  mouthFrownLeft:   'sad',
  mouthFrownRight:  'sad',
  browDownLeft:     'angry',
  browDownRight:    'angry',
  browInnerUp:      'surprised',
}

/**
 * Unity HumanBodyBones name → VRM 1.0 humanoid bone name.
 * Unity uses PascalCase; VRM 1.0 uses camelCase.
 */
const UNITY_TO_VRM_BONE = {
  Hips: 'hips', Spine: 'spine', Chest: 'chest', UpperChest: 'upperChest',
  Neck: 'neck', Head: 'head',
  LeftShoulder: 'leftShoulder',   LeftUpperArm: 'leftUpperArm',
  LeftLowerArm: 'leftLowerArm',   LeftHand:     'leftHand',
  RightShoulder: 'rightShoulder', RightUpperArm: 'rightUpperArm',
  RightLowerArm: 'rightLowerArm', RightHand:     'rightHand',
  LeftUpperLeg:  'leftUpperLeg',  LeftLowerLeg:  'leftLowerLeg',
  LeftFoot:      'leftFoot',      LeftToes:      'leftToes',
  RightUpperLeg: 'rightUpperLeg', RightLowerLeg: 'rightLowerLeg',
  RightFoot:     'rightFoot',     RightToes:     'rightToes',
  // Fingers — left
  LeftThumbProximal:      'leftThumbProximal',
  LeftThumbIntermediate:  'leftThumbIntermediate',
  LeftThumbDistal:        'leftThumbDistal',
  LeftIndexProximal:      'leftIndexProximal',
  LeftIndexIntermediate:  'leftIndexIntermediate',
  LeftIndexDistal:        'leftIndexDistal',
  LeftMiddleProximal:     'leftMiddleProximal',
  LeftMiddleIntermediate: 'leftMiddleIntermediate',
  LeftMiddleDistal:       'leftMiddleDistal',
  LeftRingProximal:       'leftRingProximal',
  LeftRingIntermediate:   'leftRingIntermediate',
  LeftRingDistal:         'leftRingDistal',
  LeftLittleProximal:     'leftLittleProximal',
  LeftLittleIntermediate: 'leftLittleIntermediate',
  LeftLittleDistal:       'leftLittleDistal',
  // Fingers — right
  RightThumbProximal:      'rightThumbProximal',
  RightThumbIntermediate:  'rightThumbIntermediate',
  RightThumbDistal:        'rightThumbDistal',
  RightIndexProximal:      'rightIndexProximal',
  RightIndexIntermediate:  'rightIndexIntermediate',
  RightIndexDistal:        'rightIndexDistal',
  RightMiddleProximal:     'rightMiddleProximal',
  RightMiddleIntermediate: 'rightMiddleIntermediate',
  RightMiddleDistal:       'rightMiddleDistal',
  RightRingProximal:       'rightRingProximal',
  RightRingIntermediate:   'rightRingIntermediate',
  RightRingDistal:         'rightRingDistal',
  RightLittleProximal:     'rightLittleProximal',
  RightLittleIntermediate: 'rightLittleIntermediate',
  RightLittleDistal:       'rightLittleDistal',
}

// ── VRMViewer class ───────────────────────────────────────────────────────────

export class VRMViewer {
  /** @type {THREE.WebGLRenderer} */ #renderer
  /** @type {THREE.Scene} */         #scene
  /** @type {THREE.PerspectiveCamera} */ #camera
  /** @type {OrbitControls|null} */  #controls = null
  /** @type {THREE.Clock} */         #clock
  /** @type {import('@pixiv/three-vrm').VRM|null} */ #vrm = null
  #loader
  #rafId = null
  #latestTracking = null
  #fpsCallback = null
  #frameCount = 0
  #fpsTimer
  #blendShapeDebugDone = false
  /** Smoothed blend-shape values — keyed by VRM expression name. */
  #smoothBlend = new Map()
  /**
   * EMA smoothing factor for facial expressions (0 = frozen, 1 = instant).
   * Lower = smoother but adds latency. 0.4 is a good default.
   */
  #blendAlpha = 0.4

  /**
   * @param {HTMLCanvasElement} canvas
   * @param {{ enableControls?: boolean }} [opts]
   *   enableControls — set false on the room viewer page so avatars can't
   *   be accidentally orbited during a stream. Default: true.
   */
  constructor(canvas, { enableControls = true } = {}) {
    // ── Renderer ──
    this.#renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true })
    this.#renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.#renderer.outputColorSpace = THREE.SRGBColorSpace
    this.#renderer.shadowMap.enabled = false   // no shadows needed, saves GPU time

    // ── Scene ──
    this.#scene = new THREE.Scene()
    this.#scene.background = new THREE.Color(0x111111)

    // ── Camera ──
    // Narrow FOV (~30°) gives a flattering portrait look for a VTuber.
    this.#camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100)
    this.#camera.position.set(0, 1.45, 2.6)

    // ── Orbit controls ── (lets the viewer rotate/zoom during a stream)
    // Disabled on the room viewer page to prevent accidental camera movement.
    if (enableControls) {
      this.#controls = new OrbitControls(this.#camera, canvas)
      this.#controls.target.set(0, 1.2, 0)
      this.#controls.enableDamping = true
      this.#controls.dampingFactor = 0.08
      this.#controls.minDistance = 0.5
      this.#controls.maxDistance = 8
      this.#controls.update()
    }

    // ── Lighting ──
    // Soft three-point rig that works for most avatar styles.
    this.#scene.add(new THREE.AmbientLight(0xffffff, 0.65))

    const key = new THREE.DirectionalLight(0xffffff, 0.9)
    key.position.set(1.5, 3, 2)
    this.#scene.add(key)

    const fill = new THREE.DirectionalLight(0xcce8ff, 0.35)
    fill.position.set(-2, 1.5, 1)
    this.#scene.add(fill)

    const rim = new THREE.DirectionalLight(0xffeedd, 0.25)
    rim.position.set(0, 2, -3)
    this.#scene.add(rim)

    // ── GLTF/VRM loader ──
    this.#loader = new GLTFLoader()
    this.#loader.register(parser => new VRMLoaderPlugin(parser))

    this.#clock = new THREE.Clock()

    // ── Resize handling ──
    const resizeObserver = new ResizeObserver(() => this.#resize(canvas))
    resizeObserver.observe(canvas.parentElement ?? document.body)
    this.#resize(canvas)

    // ── FPS counter (fires callback once per second) ──
    this.#fpsTimer = setInterval(() => {
      this.#fpsCallback?.(this.#frameCount)
      this.#frameCount = 0
    }, 1000)

    this.#startLoop()
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  /**
   * Load a VRM model from a URL.
   * @param {string} url
   * @param {(pct: number, phase: string) => void} [onProgress]
   * @returns {Promise<import('@pixiv/three-vrm').VRM>}
   */
  async loadVRM(url, onProgress) {
    // Dispose the old model before loading a new one
    if (this.#vrm) {
      this.#scene.remove(this.#vrm.scene)
      VRMUtils.deepDispose(this.#vrm.scene)
      this.#vrm = null
    }

    const gltf = await this.#loader.loadAsync(url, (ev) => {
      if (ev.total > 0) {
        const pct = Math.round((ev.loaded / ev.total) * 100)
        onProgress?.(pct, 'Downloading')
      }
    })

    onProgress?.(100, 'Parsing')

    const vrm = gltf.userData.vrm

    // removeUnnecessaryJoints is fast (prunes unused skeleton nodes).
    // removeUnnecessaryVertices is intentionally skipped — it scans all
    // geometry synchronously and can freeze the tab for several seconds on
    // complex models. Run it as an offline pre-processing step if needed.
    VRMUtils.removeUnnecessaryJoints(gltf.scene)

    this.#vrm = vrm
    this.#scene.add(vrm.scene)
    return vrm
  }

  /**
   * Queue the latest tracking frame to be applied on the next render tick.
   * @param {object} data  Parsed tracking JSON from the relay server
   */
  applyTracking(data) {
    this.#latestTracking = data
  }

  /**
   * Change the scene background.
   * @param {string} value  CSS colour string or 'transparent'
   */
  setBackground(value) {
    this.#scene.background = value === 'transparent' ? null : new THREE.Color(value)
  }

  /** Register a callback that fires each second with the current FPS. */
  onFPS(cb) { this.#fpsCallback = cb }

  /**
   * Set the expression smoothing amount.
   * @param {number} alpha  0 = fully smoothed (frozen), 1 = instant (no smoothing)
   */
  setBlendSmoothing(alpha) { this.#blendAlpha = Math.max(0, Math.min(1, alpha)) }

  dispose() {
    this.#rafId !== null && cancelAnimationFrame(this.#rafId)
    clearInterval(this.#fpsTimer)
    if (this.#vrm) VRMUtils.deepDispose(this.#vrm.scene)
    this.#controls?.dispose()
    this.#renderer.dispose()
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  /** Apply the latest tracking frame to the loaded VRM. */
  #applyTracking() {
    const vrm = this.#vrm
    const data = this.#latestTracking
    if (!vrm || !data) return

    const humanoid = vrm.humanoid
    const exprMgr  = vrm.expressionManager

    // ── Bone rotations ──
    if (data.bones) {
      for (const [unityName, rot] of Object.entries(data.bones)) {
        const vrmName = UNITY_TO_VRM_BONE[unityName]
        if (!vrmName) continue

        const node = humanoid?.getNormalizedBoneNode(vrmName)
        if (!node) continue

        // Unity (left-handed) → Three.js (right-handed): negate X only
        node.quaternion.set(-rot.qx, rot.qy, rot.qz, rot.qw)
      }
    }

    // ── Root transform (world position of the hips) ──
    if (data.root) {
      const r = data.root
      // Negate X for position (mirror across YZ)
      vrm.scene.position.set(-r.x, r.y, r.z)
      // Negate X only for rotation
      vrm.scene.quaternion.set(-r.qx, r.qy, r.qz, r.qw)
    }

    // ── Expressions / blend shapes ──
    if (data.blendShapes && exprMgr) {
      // One-time debug: log what's arriving vs what the model supports
      if (!this.#blendShapeDebugDone && Object.keys(data.blendShapes).length > 0) {
        this.#blendShapeDebugDone = true
        const vmcNames  = Object.keys(data.blendShapes)
        const vrmNames  = exprMgr.expressionMap
          ? Object.keys(exprMgr.expressionMap)
          : []
        console.log('[blend] VMC names arriving from tracker:', vmcNames)
        console.log('[blend] VRM expressions available on this model:', vrmNames)
        console.log('[blend] Current blendShapes values:', data.blendShapes)
        // Show which names have no explicit mapping and also aren't a known VRM expression
        const unmapped = vmcNames.filter(n => !(n in VMC_TO_EXPR) && !vrmNames.includes(n))
        if (unmapped.length) console.warn('[blend] Unmapped VMC/ARKit names:', unmapped)
      }

      for (const [vmcName, rawValue] of Object.entries(data.blendShapes)) {
        // Map VMC name → VRM 1.0 name; fall back to the name as-is
        // (VRM 1.0 camelCase names from Warudo already match and must not be lowercased)
        const exprName = VMC_TO_EXPR[vmcName] ?? vmcName

        // EMA low-pass filter — removes high-frequency jitter from face tracking
        const prev     = this.#smoothBlend.get(exprName) ?? rawValue
        const smoothed = prev + this.#blendAlpha * (rawValue - prev)
        this.#smoothBlend.set(exprName, smoothed)

        try { exprMgr.setValue(exprName, smoothed) } catch { /* name not on this model */ }
      }
      exprMgr.update()
    } else if (data.blendShapes && !exprMgr && !this.#blendShapeDebugDone) {
      this.#blendShapeDebugDone = true
      console.warn('[blend] blendShapes arrived but this VRM has no expressionManager')
    }
  }

  #startLoop() {
    const tick = () => {
      this.#rafId = requestAnimationFrame(tick)
      const delta = this.#clock.getDelta()

      this.#applyTracking()

      // Spring bones (hair, clothing), expression blending, etc.
      this.#vrm?.update(delta)

      this.#controls?.update()
      this.#renderer.render(this.#scene, this.#camera)
      this.#frameCount++
    }
    tick()
  }

  #resize(canvas) {
    const w = canvas.clientWidth  || canvas.parentElement?.clientWidth  || 800
    const h = canvas.clientHeight || canvas.parentElement?.clientHeight || 600
    this.#renderer.setSize(w, h, false)
    this.#camera.aspect = w / h
    this.#camera.updateProjectionMatrix()
  }
}
